import { HOST_ID } from './contract';

/**
 * Finding "a block of text" on a school's page, for text alignment and read
 * aloud. Themes put text everywhere: in paragraphs and headings, but just as
 * often straight inside a <div> or behind a link (`<div class="title"><a>…`).
 * So instead of a list of tags, each piece of text is walked up to the nearest
 * block-level box that lays it out. That box is the block.
 */

/** Never text, or not text a visitor reads. */
export const SKIP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'TEMPLATE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'CANVAS',
  'VIDEO',
  'AUDIO',
  'IMG',
  'PICTURE',
  'SOURCE',
  'TRACK',
  'BR',
  'HR',
  'WBR',
  'META',
  'LINK',
  'HEAD',
  'TITLE',
  'BASE',
  'PARAM',
  'MAP',
  'AREA',
  'SVG',
]);

/** Form controls hold text without a text node child. */
export const CONTROL_TAGS = new Set([
  'INPUT',
  'TEXTAREA',
  'SELECT',
  'BUTTON',
  'OPTION',
]);

const BLOCK_DISPLAY =
  /^(block|list-item|table-cell|table-caption|flex|grid|flow-root)$/;

/** A box that lays out its own lines of text. */
export function isBlockDisplay(display: string): boolean {
  return BLOCK_DISPLAY.test(display);
}

export interface TextBlock {
  el: HTMLElement;
  /** Its text nodes, plus images with a description, in reading order. */
  parts: Array<Text | HTMLImageElement>;
}

export interface CollectOptions {
  /**
   * How far up a block may be found. Default `root`. Pass `document.body`
   * when `root` is a node just added inside existing text, so its block can
   * be the paragraph around it.
   */
  within?: Element;
  /**
   * Keep text in display:none boxes, with the hidden box as its block.
   * Alignment wants this (a dropdown or accordion shown later should already
   * be aligned); read aloud does not.
   */
  includeHidden?: boolean;
}

/**
 * Every block of text under `root`, in document order. A block is the nearest
 * block-level ancestor of a run of text; an image with alt text counts as a
 * run of text. Text inside form controls and the widget itself is left out,
 * and so is text in display:none boxes unless `includeHidden` is set.
 */
export function collectTextBlocks(
  root: Element,
  options: CollectOptions = {},
): TextBlock[] {
  const blocks = new Map<HTMLElement, TextBlock>();
  walkText(root, options, (el, part) => {
    let entry = blocks.get(el);
    if (!entry) {
      entry = { el, parts: [] };
      blocks.set(el, entry);
    }
    entry.parts.push(part);
  });
  const out: TextBlock[] = [];
  blocks.forEach((block) => out.push(block));
  return out;
}

/**
 * The same text, as runs in reading order: a block that is interrupted by a
 * nested block (`<div>Intro <p>Para</p> outro</div>`) gives two runs, before
 * and after, so read aloud says "Intro", "Para", "outro" in that order.
 */
export function collectTextRuns(
  root: Element,
  options: CollectOptions = {},
): TextBlock[] {
  const runs: TextBlock[] = [];
  walkText(root, options, (el, part) => {
    const last = runs[runs.length - 1];
    if (last && last.el === el) last.parts.push(part);
    else runs.push({ el, parts: [part] });
  });
  return runs;
}

function walkText(
  root: Element,
  options: CollectOptions,
  visit: (el: HTMLElement, part: Text | HTMLImageElement) => void,
): void {
  const doc = root.ownerDocument || document;
  const view = doc.defaultView || window;
  const displays = new Map<Element, string>();
  const display = (el: Element) => {
    let d = displays.get(el);
    if (d === undefined) {
      d = view.getComputedStyle(el).display;
      displays.set(el, d);
    }
    return d;
  };

  const stop = (options.within || root).parentElement;

  // Show text and elements: elements only to catch <img alt>.
  const walker = doc.createTreeWalker(root, 1 | 4);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    let part: Text | HTMLImageElement;
    if (node.nodeType === 3) {
      if (!node.nodeValue || !node.nodeValue.trim()) continue;
      part = node as Text;
    } else if (
      (node as Element).tagName === 'IMG' &&
      ((node as HTMLImageElement).getAttribute('alt') || '').trim()
    ) {
      part = node as HTMLImageElement;
    } else {
      continue;
    }

    // Climb past inline boxes to the block that lays this text out.
    let el: Element | null =
      node.nodeType === 3
        ? node.parentElement
        : (node as Element).parentElement;
    while (el && el !== stop) {
      const tag = el.tagName.toUpperCase();
      if (
        (SKIP_TAGS.has(tag) && tag !== 'PICTURE') ||
        CONTROL_TAGS.has(tag) ||
        el.id === HOST_ID
      ) {
        el = null;
        break;
      }
      const d = display(el);
      if (d === 'none') {
        if (!options.includeHidden) el = null;
        break;
      }
      if (isBlockDisplay(d)) break;
      el = el.parentElement;
    }
    if (!el || el === stop || !(el instanceof (view as any).HTMLElement))
      continue;

    visit(el as HTMLElement, part);
  }
}

/**
 * The text a block lays out itself, without walking into nested blocks: for
 * pointing at one block, where walking a whole section to keep only its own
 * runs would be wasted work. Same rules as collectTextBlocks for what counts.
 */
export function ownText(block: Element): Array<Text | HTMLImageElement> {
  const doc = block.ownerDocument || document;
  const view = doc.defaultView || window;
  const parts: Array<Text | HTMLImageElement> = [];
  const walker = doc.createTreeWalker(block, 1 | 4, {
    acceptNode(node: Node) {
      if (node.nodeType === 3)
        return node.nodeValue && node.nodeValue.trim() ? 1 : 3; // ACCEPT : SKIP
      const el = node as Element;
      const tag = el.tagName.toUpperCase();
      if (tag === 'IMG') return (el.getAttribute('alt') || '').trim() ? 1 : 2; // ACCEPT : REJECT
      if (
        (SKIP_TAGS.has(tag) && tag !== 'PICTURE') ||
        CONTROL_TAGS.has(tag) ||
        el.id === HOST_ID
      )
        return 2; // REJECT: skip the subtree
      const d = view.getComputedStyle(el).display;
      if (d === 'none' || isBlockDisplay(d)) return 2; // a nested block: its own
      return 3; // SKIP: an inline box, look inside
    },
  });
  for (let n = walker.nextNode(); n; n = walker.nextNode())
    parts.push(n as Text | HTMLImageElement);
  return parts;
}

/** Links, buttons and other controls a click or a key acts on. */
export const INTERACTIVE =
  'a[href], button, input, select, textarea, option, label, summary, ' +
  '[role="button"], [role="link"], [role="tab"], [role="menuitem"], ' +
  '[role="switch"], [role="checkbox"], [role="radio"], [contenteditable=""], ' +
  '[contenteditable="true"]';
